<template>
  <BrokenContextElement
    v-if="feedMapIsBroken"
    component-name="feed"
    :problems="feedMapProblems"
  />
  <iColumn
    v-else-if="(editMode || inBuilderEditScope) && previewCardTiles.length > 0"
    width="fill"
    height="hug"
    gap="none"
  >
    <SmartFeedFilterPillSelect
      v-if="showFilterRow"
      :items="filterItems"
      :model-value="activeFilterValue"
      :label="filterLabel"
      :icon="filterIcon"
      :align="filterRowAlignment"
      :label-position="filterLabelPosition"
      :style-overrides="filterRowStyleOverrides"
      :pill-style-overrides="filterPillStyleOverrides"
      :label-style-overrides="filterLabelStyleOverrides"
      :icon-style-overrides="filterIconStyleOverrides"
      single-select
      include-all-pill
      inactive-variant="tertiary"
      pill-wrap="nowrap"
      data-test="feed-filter-chips"
    />
    <component
      :is="wrapperComponent"
      v-bind="wrapperComponentProps"
    >
      <template
        v-for="tile in previewTilesWithSeparators"
        :key="tile.key"
      >
        <StudioFeedRowItem
          v-if="tile.kind === 'section' && tile.componentId > 0"
          :entity-id="0"
          :dynamic-component-id="tile.componentId"
          :separator-label="tile.label"
          :theme-mode="themeMode"
          edit-mode
          span-full-row
          data-test="feed-separator-header"
        /><iDivider
          v-else-if="tile.kind === 'section'"
          span-full-row
          data-test="feed-separator-divider"
        /><StudioFeedRowItem
          v-else
          :entity-id="tile.tileIndex + 1"
          :dynamic-component-id="tile.cardId"
          :theme-mode="themeMode"
          edit-mode
          size-to-card
        />
      </template>
    </component>
  </iColumn>
  <iColumn
    v-else-if="editMode || inBuilderEditScope"
    width="fill"
    height="hug"
    gap="none"
  >
    <SmartFeedFilterPillSelect
      v-if="showFilterRow"
      :items="filterItems"
      :model-value="activeFilterValue"
      :label="filterLabel"
      :icon="filterIcon"
      :align="filterRowAlignment"
      :label-position="filterLabelPosition"
      :style-overrides="filterRowStyleOverrides"
      :pill-style-overrides="filterPillStyleOverrides"
      :label-style-overrides="filterLabelStyleOverrides"
      :icon-style-overrides="filterIconStyleOverrides"
      single-select
      include-all-pill
      inactive-variant="tertiary"
      pill-wrap="nowrap"
      data-test="feed-filter-chips"
    />
    <SmartFeedPlaceholder
      :smart-feed-layout="getOptionValue(SmartFeedLayout)"
      entity-layout="cardLarge"
    />
  </iColumn>
  <iColumn
    v-else-if="!omitBecauseSettledEmpty"
    width="fill"
    :height="feedColumnHeight"
    :style-overrides="appInputWrapperStyleOverrides"
  >
    <SmartFeedFilterPillSelect
      v-if="showFilterRow"
      :items="filterItems"
      :model-value="activeFilterValue"
      :label="filterLabel"
      :icon="filterIcon"
      :align="filterRowAlignment"
      :label-position="filterLabelPosition"
      :style-overrides="filterRowStyleOverrides"
      :pill-style-overrides="filterPillStyleOverrides"
      :label-style-overrides="filterLabelStyleOverrides"
      :icon-style-overrides="filterIconStyleOverrides"
      single-select
      include-all-pill
      inactive-variant="tertiary"
      pill-wrap="nowrap"
      data-test="feed-filter-chips"
      @update:model-value="selectFilterValue"
    />
    <component
      :is="scrollComponent"
      v-bind="scrollProps"
      v-on="scrollEvents"
    >
      <component
        :is="wrapperComponent"
        v-if="layout && entities.length > 0"
        v-bind="wrapperComponentProps"
        v-on="wrapperComponentEvents"
      >
        <template
          v-for="item in rowsWithSeparators"
          :key="item.key"
        >
          <StudioFeedRowItem
            v-if="item.kind === 'section' && item.componentId > 0"
            :entity-id="0"
            :dynamic-component-id="item.componentId"
            :separator-label="item.label"
            :theme-mode="themeMode"
            :edit-mode="editMode"
            span-full-row
            data-test="feed-separator-header"
            :data-read-position-separator="item.readPosition ? 'true' : null"
          /><iDivider
            v-else-if="item.kind === 'section'"
            span-full-row
            data-test="feed-separator-divider"
            :data-read-position-separator="item.readPosition ? 'true' : null"
          /><AppComponentOverrideHost
            v-else
            v-bind="rowBindings(item.entity)"
            :app-name="appName"
            :dynamic-component-id="rowComponentId(item.entity)"
            :contexts="getItemContexts(item.entity)"
            :theme-mode="themeMode"
          />
        </template>
      </component>
      <slot v-else-if="!isLoadingNext" name="empty">
        <StudioFeedRowItem
          v-if="emptyStateComponentId > 0"
          :entity-id="0"
          :dynamic-component-id="emptyStateComponentId"
          :theme-mode="themeMode"
          :edit-mode="editMode"
          size-to-card
          data-test="feed-empty-state-component"
        />
        <iRow
          v-else
          width="fill"
          height="hug"
          align="center"
          vertical-align="middle"
          :style-overrides="{
            ...resolveSpacingOverrides('standard', 'padding'),
          }"
        >
          <iText>{{ getOptionValue(EmptyStateText) }}</iText>
        </iRow>
      </slot>
    </component>
  </iColumn>
</template>

<script>
import { renderSurfaceKind, isCompiledSurfaceKind }
  from "%/dynamic-component-editor/helpers/context-error-report";
import AppComponentOverrideHost
  from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/AppComponentOverrideHost.vue";
import BrokenContextElement from "%/dynamic-component-editor/components/BrokenContextElement.vue";
import FeedComponentsMap from "%/dynamic-component-editor/classes/feed-components-map";
import RowTypeField from "%/dynamic-component-editor/options/row-type-field";
import { ContextValidationError } from "@bloglovin/inspire-engine/Exceptions/ContextValidationError.js";
import { getActivePinia } from "pinia";
import {
  buildComponentContexts,
  ambientContextNames,
} from "@bloglovin/inspire-engine/DynamicComponents/Helpers/build-component-contexts.js";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-component-editor/mixins/dynamic-element-with-context-mixin";
import App from "@bloglovin/inspire-engine/App";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import LayoutGap from "%/dynamic-component-editor/options/layout-gap";
import normalizeGapValue from "%/dynamic-component-editor/helpers/gap-style-value";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import AppFeedOptions from "%/dynamic-component-editor/bundles/app-feed-options";
import FeedEmptyStateOptions from "%/dynamic-component-editor/bundles/feed-empty-state-options";
import FeedSeparatorsOptions from "%/dynamic-component-editor/bundles/feed-separators-options";
import FeedPlaybackOptions from "%/dynamic-component-editor/bundles/feed-playback-options";
import FeedVideoAutoplay from "%/dynamic-component-editor/options/feed-video-autoplay";
import FeedFiltersOptions from "%/dynamic-component-editor/bundles/feed-filters-options";
import FeedFiltersEnabled from "%/dynamic-component-editor/options/feed-filters-enabled";
import FilterLabelText from "%/dynamic-component-editor/options/filter-label-text";
import FilterIcon from "%/dynamic-component-editor/options/filter-icon";
import FilterToolbarAlignment from "%/dynamic-component-editor/options/filter-toolbar-alignment";
import FilterToolbarLabelPosition from "%/dynamic-component-editor/options/filter-toolbar-label-position";
import FilterToolbarBackgroundColor from "%/dynamic-component-editor/options/filter-toolbar-background-color";
import FilterToolbarFontColor from "%/dynamic-component-editor/options/filter-toolbar-font-color";
import FilterToolbarUnselectedFontColor from "%/dynamic-component-editor/options/filter-toolbar-unselected-font-color";
import FilterToolbarIconColor from "%/dynamic-component-editor/options/filter-toolbar-icon-color";
import FilterToolbarPadding from "%/dynamic-component-editor/options/filter-toolbar-padding";
import FilterToolbarMargins from "%/dynamic-component-editor/options/filter-toolbar-margins";
import SmartFeedFilterPillSelect from "SmartFeed/filters/SmartFeedFilterPillSelect.vue";
import SmartFeedPlaceholder from "SmartFeed/components/SmartFeedPlaceholder.vue";
import StudioFeedRowItem from "%/dynamic-component-editor/standard-elements/StudioFeedRowItem.vue";
import SmartFeedLayout from "%/dynamic-component-editor/options/smart-feed-layout";
import ShowDots from "%/dynamic-component-editor/options/show-dots";
import SmartFeedPagination from "%/dynamic-component-editor/options/smart-feed-pagination";
import SmartFeedColumnMinWidth from "%/dynamic-component-editor/options/smart-feed-column-min-width";
import SmartFeedColumnMaxWidth from "%/dynamic-component-editor/options/smart-feed-column-max-width";
import SmartFeedCardAlignment from "%/dynamic-component-editor/options/smart-feed-card-alignment";
import SmartFeedPageLimit from "%/dynamic-component-editor/options/smart-feed-page-limit";
import SmartFeedSeeMoreText from "%/dynamic-component-editor/options/smart-feed-see-more-text";
import SmartFeedSeeMoreAlignment from "%/dynamic-component-editor/options/smart-feed-see-more-alignment";
import EmptyStateText from "%/dynamic-component-editor/options/empty-state-text";
import OmitIfEmpty from "%/dynamic-component-editor/options/omit-if-empty";
import EmptyStateComponentId from "%/dynamic-component-editor/options/empty-state-component-id";
import EmptyStateMode, { EMPTY_STATE_MODE_COMPONENT } from "%/dynamic-component-editor/options/empty-state-mode";
import FeedSeparatorsEnabled from "%/dynamic-component-editor/options/feed-separators-enabled";
import FeedSeparatorsComponentId from "%/dynamic-component-editor/options/feed-separators-component-id";
import FeedSeparatorsRules from "%/dynamic-component-editor/options/feed-separators-rules";
import FeedReadPositionEnabled from "%/dynamic-component-editor/options/feed-read-position-enabled";
import FeedReadPositionField from "%/dynamic-component-editor/options/feed-read-position-field";
import FeedReadPositionLabel from "%/dynamic-component-editor/options/feed-read-position-label";
import FeedReadPositionAction from "%/dynamic-component-editor/options/feed-read-position-action";
import FeedAnchorToBottom from "%/dynamic-component-editor/options/feed-anchor-to-bottom";
import FeedLastSeenComponentId from "%/dynamic-component-editor/options/feed-last-seen-component-id";
import FeedRowConditions from "%/dynamic-component-editor/options/feed-row-conditions";
import { conditionComponentId, rowsWithLastSeen, anchoredOrder } from "%/dynamic-component-editor/helpers/feed-row-conditions";
import { hasCompletePk } from "%/dynamic-component-editor/helpers/complete-pk";
import { insertReadPositionSection } from "%/dynamic-component-editor/classes/feed-read-position";
import {
  readPositionPointer,
  readPositionAnchor,
  readPositionShouldMarkRead,
  readPositionDecided,
} from "@bloglovin/inspire-engine/Helpers/feedReadPosition.js";
import { buildBetweenComponentConfigs } from "%/dynamic-component-editor/classes/feed-separator-rule";
import { LABEL_SCOPE, previewLabel } from "%/dynamic-component-editor/classes/feed-separator-label";
import dayjs from "dayjs";
import { resolveOneParam } from "@bloglovin/inspire-engine/Helpers/resolveActionParams.js";
import LogicExpression from "@bloglovin/inspire-engine/Helpers/LogicExpression.js";
import AppFeedComponents from "%/dynamic-component-editor/options/app-feed-components";
import { Context } from "%/dynamic-component-editor/classes/context-v2";
import DynamicAppInputOptionsMixin from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/dynamic-app-input-options-mixin";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderWidth from "%/dynamic-component-editor/options/border-width";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import { readRequiredContexts } from "@studio/Helpers/studio-installed-apps-bootstrap";
import { useDynamicComponentEditorStore }
  from "%/dynamic-component-editor/stores/dynamic-component-editor";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useCompletionAssignmentStore } from "%/dynamic-component-editor/stores/completion-assignment-store";
import { useCompletionStatusStore } from "%/dynamic-component-editor/stores/completion-status-store";
import { resolveCompletionStatus } from "%/dynamic-component-editor/helpers/completion-status-resolver";
import { createStableFeedRowKeys } from "%/dynamic-component-editor/helpers/stable-feed-row-key.mjs";

const HASHTAG_FILTER_NAME = "hashtagId";
const HASHTAG_IDS_FIELD = "hashtagIds";
const HASHTAG_NAMES_FIELD = "hashtags";
const HASHTAG_ID_DELIMITER = "|";

const PLACEHOLDER_FILTER_HASHTAGS = [
  { hashtagId: 1, value: "Hashtag" },
  { hashtagId: 2, value: "Another hashtag" },
];

const NO_ROW_BINDINGS = Object.freeze({});

function hashtagFilterToken(hashtagId) {
  return `${HASHTAG_ID_DELIMITER}${hashtagId}${HASHTAG_ID_DELIMITER}`;
}

function edgeOverrides(values, prefix) {
  const overrides = {};
  const edges = values || {};
  ["Top", "Right", "Bottom", "Left"].forEach(edge => {
    const value = edges[edge.toLowerCase()];
    if (value && value !== "0px") {
      overrides[`${prefix}${edge}`] = value;
    }
  });
  return overrides;
}

function isVisibleColor(value) {
  if (!value || value === "none" || value === "transparent" || value === "unset") {
    return false;
  }
  const normalized = String(value).replace(/\s+/g, "").toLowerCase();
  return !(normalized.startsWith("rgba(") && normalized.endsWith(",0)"));
}

export default {
  name: "DynamicAppFeedElement",
  components: { SmartFeedPlaceholder, StudioFeedRowItem, AppComponentOverrideHost, BrokenContextElement, SmartFeedFilterPillSelect },
  mixins: [DynamicElementMixin, DynamicElementWithContext, DynamicAppInputOptionsMixin],
  // XXX: The feed answers the one validity question for itself, so the layers
  // XXX: panel and any compile pass ask the SAME code the canvas block asks.
  // XXX: `FeedComponentsMap.readOptionValue` was called here behind a
  // XXX: `readOptionValue ? call() : null` guard while it was defined NOWHERE, so
  // XXX: the else-arm ran every time and handed problemsForOptions a null map.
  // XXX: That branch reports "no app-feed-components mapping", so EVERY feed
  // XXX: element on EVERY destination was flagged broken regardless of what it
  // XXX: stored -- while the picker modal, which reads through the store, showed
  // XXX: the real binding. See #112. Read parsedOptions, the normalised tree
  // XXX: every other consumer in the editor reads.
  configurationProblems({ element }) {
    const options = element.parsedOptions;
    return FeedComponentsMap.problemsForOptions(options, FeedComponentsMap.readOptionValue(options));
  },
  inject: {
    inBuilderEditScope: { from: "inBuilderEditScope", default: false },
  },
  provide() {
    return {
      feedVideoAutoplay: () => this.getOptionValue(FeedVideoAutoplay) === true,
    };
  },
  elementPanelSettings: {
    displayName: "Feed",
    elementGroup: "general",
    displayIcon: "feed",
    elementType: "element",
  },
  optionBundles: [
    new AppearanceOptions({}, {}, false),
    AppFeedOptions,
    new FeedSeparatorsOptions(),
    new FeedFiltersOptions(),
    new FeedEmptyStateOptions(),
    new FeedPlaybackOptions(),
    new SpacingOptions(),
  ],
  options: [
    LayoutGap,
    FeedAnchorToBottom,
    FeedLastSeenComponentId,
    FeedRowConditions,
    MaxWidth,
    MinWidth,
    RowTypeField,
    SmartFeedLayout,
    SmartFeedPagination,
    SmartFeedColumnMinWidth,
    ShowDots,
    new LayoutWidth({
      "default": "fill",
    }),
    new LayoutHeight({
      "default": "hug",
    }),
    BackgroundColor,
    BorderColor,
    BorderWidth,
  ],
  hidden: true,
  readPositionSupported: true,
  data() {
    return {
      knownFilterHashtags: [],
      activeFilterValue: "",
      readPosition: null,
    };
  },
  computed: {
    fillsHeight() {
      return this.getOptionValue(LayoutHeight) === "fill";
    },
    feedColumnHeight() {
      return this.fillsHeight ? "fillv2" : "hug";
    },
    feedScrollHeight() {
      return this.fillsHeight ? "fill" : "hug";
    },
    SmartFeedLayout() {
      return SmartFeedLayout;
    },
    EmptyStateText() {
      return EmptyStateText;
    },
    emptyStateComponentId() {
      if (this.getOptionValue(EmptyStateMode) !== EMPTY_STATE_MODE_COMPONENT) {
        return 0;
      }
      const id = Number(this.getOptionValue(EmptyStateComponentId));
      return Number.isFinite(id) && id > 0 ? id : 0;
    },
    separatorsEnabled() {
      return Boolean(this.getOptionValue(FeedSeparatorsEnabled));
    },
    separatorComponentId() {
      const id = Number(this.getOptionValue(FeedSeparatorsComponentId));
      return Number.isFinite(id) && id > 0 ? id : 0;
    },
    separatorConfigs() {
      if (!this.separatorsEnabled) {
        return [];
      }
      const rules = this.getOptionValue(FeedSeparatorsRules);
      return buildBetweenComponentConfigs(Array.isArray(rules) ? rules : [], this.separatorComponentId);
    },
    separatorsRenderable() {
      return this.separatorConfigs.length > 0 && this.smartFeedLayoutValue !== "carousel";
    },
    rowsWithSeparators() {
      const rows = insertReadPositionSection(
        this.rowsWithConditionalSeparators,
        this.smartFeedLayoutValue === "carousel" ? null : this.readPosition?.anchorPkString,
        this.readPositionSection,
        pk => App.getPkString(pk),
      );
      return anchoredOrder(rowsWithLastSeen(rows, this.editMode ? 0 : this.lastSeenComponentId), this.anchoredToBottom);
    },
    anchoredToBottom() {
      return this.getOptionValue(FeedAnchorToBottom) === true && this.smartFeedLayoutValue !== "carousel";
    },
    lastSeenComponentId() {
      return Number(this.getOptionValue(FeedLastSeenComponentId)) || 0;
    },
    scrollComponent() {
      return this.anchoredToBottom ? "iBiDirectionalInfiniteScroll" : "iInfiniteScroll";
    },
    scrollProps() {
      if (this.anchoredToBottom) {
        return {
          anchoredToBottom: true,
          enableTopLoad: true,
          enableBottomLoad: false,
          hasMoreTop: this.hasMoreNext,
          loadingTop: this.isLoadingNext,
          threshold: 1000,
        };
      }
      return {
        hasMore: this.hasMoreNext,
        isLoading: this.isLoadingNext,
        height: this.feedScrollHeight,
        scrollThreshold: 1000,
        showLoadMoreTrigger: this.smartFeedLayoutValue !== "carousel",
        loadMoreBehavior: this.loadMoreBehavior,
        loadMoreText: this.seeMoreTextValue,
        loadMoreButtonAlign: this.seeMoreAlignmentValue,
      };
    },
    scrollEvents() {
      return this.anchoredToBottom ? { "load:top": this.loadMoreNext } : { loadMore: this.loadMoreNext };
    },
    readPositionSection() {
      return {
        kind: "section",
        key: "read-position",
        label: this.getOptionValue(FeedReadPositionLabel) || FeedReadPositionLabel.defaultValue,
        componentId: this.separatorComponentId,
        readPosition: true,
      };
    },
    readPositionField() {
      if (this.editMode || this.getOptionValue(FeedReadPositionEnabled) !== true) {
        return "";
      }
      return String(this.getOptionValue(FeedReadPositionField) ?? "");
    },
    readPositionPointer() {
      if (!this.readPositionField || !this.feedApp || !this.feedContext.entityType || !this.feedContext.pk) {
        return null;
      }
      const feedApp = this.feedApp;
      const ownerOf = ref => {
        if (App.isStandardEntityType(ref.entityType)) {
          return App.getRoot();
        }
        return feedApp;
      };
      return readPositionPointer({
        readField: (ref, field) => ownerOf(ref).getEntityField(ref.entityType, ref.pk, field),
        isHydrated: ref => ownerOf(ref).getEntityField(ref.entityType, ref.pk, "isHydrated") === true,
      }, { entityType: this.feedContext.entityType, pk: this.feedContext.pk }, this.readPositionField);
    },
    readPositionCaptureReady() {
      return this.readPosition === null
        && this.feedHasSettled
        && !this.isLoadingNext
        && this.readPositionPointer?.ready === true
        && readPositionDecided(this.entities, this.readPositionPointer.pk, { hasMorePrevious: false, hasMoreNext: this.hasMoreNext });
    },
    rowsWithConditionalSeparators() {
      if (!this.separatorsRenderable) {
        return this.entities.map(entity => this.separatorRowEntry(entity));
      }
      const rows = this.entities.map(entity => this.separatorRowEntry(entity));
      if (rows.length === 0) {
        return rows;
      }
      const out = [];
      const leadingConfig = this.separatorConfigs.find(config => config.leading);
      if (leadingConfig) {
        const hit = this.pickSeparatorConfig(null, this.entities[0], true);
        if (hit) {
          out.push(this.separatorEntry(hit, this.entities[0], "lead"));
        }
      }
      rows.forEach((row, index) => {
        out.push(row);
        const next = this.entities[index + 1];
        if (!next) {
          return;
        }
        const hit = this.pickSeparatorConfig(this.entities[index], next, false);
        if (hit) {
          out.push(this.separatorEntry(hit, next, index));
        }
      });
      return out;
    },
    entityContext() {
      return this.elementContext.entityContext;
    },
    fieldName() {
      return this.elementContext.field;
    },
    feedContext() {
      const pk = this.contextItem?.entityPkUrlVars;
      return {
        entityType: this.entityType,
        pk: hasCompletePk(pk) ? pk : null,
      };
    },
    feedComponentsMap() {
      return this.getOptionValue(AppFeedComponents) ?? null;
    },
    // XXX: In the builder the store getter is THE source, so this red block and
    // XXX: the layers row are the same value. A compiled surface has no editor
    // XXX: store, so it derives the same answer from the same detector.
    feedMapProblems() {
      const options = DynamicElement.get(this.identifier)?.element?.options ?? {};
      if (this.editMode) {
        const reason = useDynamicComponentEditorStore()
            .elementMisconfigurations?.[String(this.identifier).split(":")[0]];
        return reason ? [reason] : [];
      }
      if (this.hasFieldDefRowDispatch) {
        return [];
      }
      return FeedComponentsMap.problemsForOptions(options, this.feedComponentsMap);
    },
    feedMapIsBroken() {
      return this.feedMapProblems.length > 0 && !isCompiledSurfaceKind(renderSurfaceKind());
    },
    feedMapGatePasses() {
      const problems = this.feedMapProblems;
      if (problems.length && isCompiledSurfaceKind(renderSurfaceKind())) {
        // XXX: intentional hard fail — do NOT catch or soften. A feed whose
        // XXX: component map is missing, legacy-shaped or malformed renders the
        // XXX: WRONG rows or none at all; staging must break at the broken feed
        // XXX: rather than ship a silently empty one. Re-map the feed in the
        // XXX: builder and recompile. See feed-components-map.js.
        throw new ContextValidationError("feed", problems);
      }
      return problems.length === 0;
    },
    feedFieldDef() {
      try {
        return App.get(this.contextAppName)?.getEntityFieldDef?.(this.entityType, this.fieldName) ?? null;
      } catch (e) {
        return null;
      }
    },
    fieldDefRowSelectors() {
      const list = this.feedFieldDef?.rowComponentSelector;
      return Array.isArray(list) ? list : [];
    },
    fieldDefRowContext() {
      const map = this.feedFieldDef?.rowContext;
      return map && typeof map === "object" ? map : null;
    },
    hasFieldDefRowDispatch() {
      return this.fieldDefRowSelectors.length > 0;
    },
    rowEntityType() {
      const fromApp = this.feedFieldDef?.entity;
      if (fromApp) {
        return fromApp;
      }
      const legacyField = window.blclient?.data?.legacyEntities?.[this.entityType]?.fields?.[this.fieldName]?.entity;
      if (legacyField) {
        return legacyField;
      }
      return this.contextItem?.entityType ?? null;
    },
    isStoryFeed() {
      return this.rowEntityType === "Story";
    },
    rowTypeFieldName() {
      return String(this.getOptionValue(RowTypeField) ?? "").trim();
    },
    completionPublicationId() {
      return useConfigurationStore().publicationId
        || (window.blclient?.data?.themeDestinationId ?? 0);
    },
    feedStoryType() {
      const fromApp = this.feedFieldDef?.storyType;
      if (fromApp) {
        return fromApp;
      }
      return window.blclient?.data?.legacyEntities?.[this.entityType]?.fields?.[this.fieldName]?.storyType ?? "";
    },
    previewCardComponentIds() {
      if (this.feedStoryType) {
        const hit = FeedComponentsMap.resolve(this.feedComponentsMap, {
          entityType: this.rowEntityType,
          type: this.feedStoryType,
        });
        return hit ? [hit.leaf.componentId] : [];
      }
      return [...new Set(
        FeedComponentsMap.componentReferences(this.feedComponentsMap).map(reference => reference.componentId),
      )];
    },
    previewCardTiles() {
      const cardIds = this.previewCardComponentIds;
      if (!cardIds.length) {
        return [];
      }
      return new Array(this.previewCardCount).fill("").map((_, tileIndex) => cardIds[tileIndex % cardIds.length]);
    },
    previewTilesWithSeparators() {
      const tiles = this.previewCardTiles.map((cardId, tileIndex) => ({
        kind: "tile",
        cardId,
        tileIndex,
        key: `card-preview-${tileIndex}-${cardId}`,
      }));
      if (!this.separatorsRenderable) {
        return tiles;
      }
      const config = this.separatorConfigs[0];
      return [
        {
          kind: "section",
          componentId: config.template,
          label: this.previewSeparatorLabel(config?.context?.separatorLabel?.value ?? ""),
          key: "section-preview",
        },
        ...tiles,
      ];
    },
    hasAnyRowComponent() {
      return this.hasFieldDefRowDispatch
        || FeedComponentsMap.componentReferences(this.feedComponentsMap).length > 0;
    },
    smartFeedLayoutValue() {
      return this.getOptionValue(SmartFeedLayout) || "grid";
    },
    previewCardCount() {
      return this.smartFeedLayoutValue === "list" ? 3 : 6;
    },
    loadMoreBehavior() {
      const pagination = this.getOptionValue(SmartFeedPagination);
      return SmartFeedPagination.getOptionMetaData(pagination)?.loadMoreBehavior ?? "manual";
    },
    seeMoreTextValue() {
      return this.appInputResolvedValue(this.getOptionValue(SmartFeedSeeMoreText)) ?? "Load More";
    },
    seeMoreAlignmentValue() {
      return this.appInputResolvedValue(this.getOptionValue(SmartFeedSeeMoreAlignment)) ?? "center";
    },
    pageLimitValue() {
      const raw = this.appInputValueIfSet(SmartFeedPageLimit);
      const parsed = Number(raw);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    feedGap() {
      return normalizeGapValue(this.getOptionValue(LayoutGap) || "standard");
    },
    wrapperComponent() {
      switch (this.smartFeedLayoutValue) {
        case "carousel":
          return "iCarousel";
        case "list":
          return "iColumn";
        default:
          return "iMasonry";
      }
    },
    wrapperComponentProps() {
      switch (this.smartFeedLayoutValue) {
        case "carousel":
          return {
            overflow: "visible",
            hasMore: this.hasMoreNext,
            dots: this.getOptionValue(ShowDots),
            arrows: false,
            snap: true,
            gap: this.feedGap,
          };
        case "list":
          return { gap: this.feedGap, width: "fill" };
        default:
          return {
            gap: this.feedGap,
            minColumnWidth: this.getOptionValue(SmartFeedColumnMinWidth),
            maxColumnWidth: this.getOptionValue(SmartFeedColumnMaxWidth),
            align: this.getOptionValue(SmartFeedCardAlignment),
            equalHeight: true,
          };
      }
    },
    wrapperComponentEvents() {
      if (this.smartFeedLayoutValue === "carousel") {
        return { "load-more": this.loadMoreNext };
      }
      return {};
    },
    feedApp() {
      try {
        return App.get(this.contextAppName);
      } catch {
        return null;
      }
    },
    hasMoreNext() {
      if (this.editMode || !this.feedContext.entityType || !this.feedContext.pk || !this.feedApp) {
        return false;
      }
      return Boolean(this.feedApp.getFeedMeta(this.feedContext.entityType, this.feedContext.pk, this.fieldName, "hasMoreNext", null, this.activeFilters));
    },
    isLoadingNext() {
      if (this.editMode || !this.feedContext.entityType || !this.feedContext.pk || !this.feedApp) {
        return false;
      }
      return Boolean(this.feedApp.getFeedMeta(this.feedContext.entityType, this.feedContext.pk, this.fieldName, "isLoadingNext", null, this.activeFilters));
    },
    feedHasSettled() {
      if (this.editMode || !this.feedContext.entityType || !this.feedContext.pk || !this.feedApp) {
        return false;
      }
      return Boolean(this.feedApp.getFeedMeta(this.feedContext.entityType, this.feedContext.pk, this.fieldName, "hasLoadedAtLeastOnce", null, this.activeFilters));
    },
    omitBecauseSettledEmpty() {
      return this.getOptionValue(OmitIfEmpty) === true
        && this.feedHasSettled
        && this.entities.length === 0;
    },
    // XXX: The gate is evaluated BEFORE the contextItem short-circuit, and the
    // XXX: template reads `layout` BEFORE entities.length, because both were lazy:
    // XXX: a misconfigured feed whose anchor context did not resolve never read
    // XXX: this computed, so the compiled-surface throw never fired and staging
    // XXX: rendered a silent blank instead of the error page.
    layout() {
      if (this.editMode) {
        return null;
      }
      const gatePasses = this.feedMapGatePasses;
      if (!this.contextItem) {
        return null;
      }
      return gatePasses && this.hasAnyRowComponent;
    },
    enclosingRowContexts() {
      const scoped = Object.fromEntries(
        Object.entries(this.scopedProvidedContexts?.() ?? {}).map(([contextName, scopedItem]) => [
          contextName,
          scopedItem !== null && typeof scopedItem === "object"
            ? { entityType: scopedItem.entityType, pk: scopedItem.entityPkUrlVars }
            : scopedItem,
        ]),
      );
      return { ...Context.contexts, ...scoped };
    },
    filtersEnabled() {
      return this.getOptionValue(FeedFiltersEnabled) === true;
    },
    hashtagFilterDeclaration() {
      if (!this.feedApp || !this.feedContext.entityType || !this.fieldName) {
        return null;
      }
      let fieldDef = null;
      try {
        fieldDef = this.feedApp.getEntityFieldDef(this.feedContext.entityType, this.fieldName);
      } catch {
        return null;
      }
      return fieldDef?.filters?.[HASHTAG_FILTER_NAME] ?? null;
    },
    activeFilters() {
      if (!this.filtersEnabled || !this.hashtagFilterDeclaration) {
        return null;
      }
      if (this.activeFilterValue === "") {
        return {};
      }
      return { [HASHTAG_FILTER_NAME]: this.activeFilterValue };
    },
    loadedRowHashtags() {
      if (this.editMode || this.inBuilderEditScope) {
        return PLACEHOLDER_FILTER_HASHTAGS;
      }
      const byId = new Map();
      this.entities.forEach(item => {
        this.rowHashtags(item).forEach(hashtag => {
          const id = Number(hashtag?.hashtagId);
          if (Number.isFinite(id) && id > 0 && !byId.has(id)) {
            byId.set(id, { hashtagId: id, value: String(hashtag.value ?? "") });
          }
        });
      });
      return [...byId.values()];
    },
    filterHashtags() {
      if (this.activeFilterValue === "" || this.knownFilterHashtags.length === 0) {
        return this.loadedRowHashtags;
      }
      return this.knownFilterHashtags;
    },
    filterItems() {
      return this.filterHashtags.map(hashtag => ({
        label: hashtag.value,
        value: hashtagFilterToken(hashtag.hashtagId),
      }));
    },
    showFilterRow() {
      if (this.editMode || this.inBuilderEditScope) {
        return this.filtersEnabled && this.filterItems.length > 0;
      }
      return this.filtersEnabled && this.hashtagFilterDeclaration !== null && this.filterItems.length > 0;
    },
    filterRowAlignment() {
      return this.getOptionValue(FilterToolbarAlignment) || "left";
    },
    filterLabelPosition() {
      return this.getOptionValue(FilterToolbarLabelPosition) || "inline";
    },
    filterRowStyleOverrides() {
      return edgeOverrides(this.getOptionValue(FilterToolbarMargins), "margin");
    },
    filterPillStyleOverrides() {
      const overrides = {};
      const backgroundColor = this.getOptionValue(FilterToolbarBackgroundColor);
      if (isVisibleColor(backgroundColor)) {
        overrides.primaryVariantBackgroundColor = backgroundColor;
        overrides.primaryVariantHoverBackgroundColor = backgroundColor;
      }
      const fontColor = this.getOptionValue(FilterToolbarFontColor);
      if (fontColor && isVisibleColor(backgroundColor)) {
        overrides.primaryVariantFontColor = fontColor;
        overrides.primaryVariantHoverFontColor = fontColor;
      }
      const unselectedFontColor = this.getOptionValue(FilterToolbarUnselectedFontColor);
      if (unselectedFontColor) {
        overrides.tertiaryVariantFontColor = unselectedFontColor;
        overrides.tertiaryVariantHoverFontColor = unselectedFontColor;
      }
      return { ...overrides, ...edgeOverrides(this.getOptionValue(FilterToolbarPadding), "smallPadding") };
    },
    filterLabelStyleOverrides() {
      const fontColor = this.getOptionValue(FilterToolbarUnselectedFontColor);
      if (!fontColor) {
        return {};
      }
      return { standardFontColor: fontColor, standardHoverFontColor: fontColor };
    },
    filterIconStyleOverrides() {
      return { primaryVariantColor: this.getOptionValue(FilterToolbarIconColor) || "standard" };
    },
    filterLabel() {
      return this.getOptionValue(FilterLabelText) ?? "";
    },
    filterIcon() {
      return this.getOptionValue(FilterIcon) ?? "";
    },
    entities() {
      if (this.editMode) {
        return Array.from({ length: this.previewCardCount }, (_, index) => index + 1);
      }
      if (!this.feedContext.entityType || !this.feedContext.pk || !this.feedApp) {
        return []; // unresolved context / app not booted — degrade to empty state
      }
      const app = this.feedApp;
      const context = this.feedContext;
      const itemsWithoutPkString = app.getFeedItems(context.entityType, context.pk, this.fieldName, null, this.activeFilters);
      return itemsWithoutPkString.filter(item => this.rowIsHydrated(item)).map(item => {
        item.pkString = App.getPkString(item.entityPk);
        return item;
      });
    },
  },
  watch: {
    readPositionCaptureReady: {
      immediate: true,
      handler(ready) {
        if (ready) {
          this.captureReadPosition();
        }
      },
    },
    loadedRowHashtags: {
      immediate: true,
      handler(hashtags) {
        if (this.activeFilterValue === "" && hashtags.length > 0) {
          this.knownFilterHashtags = hashtags;
        }
      },
    },
    "feedContext.pk": {
      immediate: true,
      handler(pk) {
        if (this.editMode || !pk || !this.feedContext.entityType) {
          return;
        }
        let app = null;
        try {
          app = App.get(this.contextAppName);
        } catch {
          return;
        }
        app.setFeedPageLimit(this.feedContext.entityType, pk, this.fieldName, this.pageLimitValue);
        app.triggerFeedInitialLoad(this.feedContext.entityType, pk, this.fieldName, null, this.activeFilters);
      },
    },
    activeFilters(filters, previous) {
      if (this.editMode || !this.feedContext.pk || !this.feedContext.entityType || !this.feedApp) {
        return;
      }
      if (JSON.stringify(filters) === JSON.stringify(previous)) {
        return;
      }
      this.feedApp.triggerFeedInitialLoad(this.feedContext.entityType, this.feedContext.pk, this.fieldName, null, filters);
    },
    isStoryFeed: {
      immediate: true,
      handler(isStoryFeed) {
        if (!isStoryFeed) {
          return;
        }
        const publicationId = this.completionPublicationId;
        if (publicationId) {
          useCompletionAssignmentStore().ensureStoryTypeCardAssignments(publicationId);
        }
        if (publicationId && !this.editMode && !this.inBuilderEditScope) {
          useCompletionAssignmentStore().ensureSubVariantAssignments(publicationId);
          useCompletionAssignmentStore().ensureTwoStateMode(publicationId);
        }
      },
    },
    entities: {
      immediate: true,
      handler(items) {
        if (this.editMode || !this.isStoryFeed || !Array.isArray(items)) {
          return;
        }
        let app = null;
        try {
          app = App.get(this.contextAppName);
        } catch {
          return;
        }
        const store = useCompletionStatusStore();
        for (const item of items) {
          const storyId = Number(item?.entityPk?.id) || 0;
          if (storyId && app?.getEntity(this.rowEntityType, { id: String(storyId) })?.isFolder) {
            store.ensureFolderProgress(storyId);
          }
        }
      },
    },
  },
  created() {
    this.rowKeys = createStableFeedRowKeys((entityType, realPk) => this.feedApp?.tempPkStringForRealPk(entityType, realPk) ?? null);
  },
  methods: {
    rowBindings() {
      return NO_ROW_BINDINGS;
    },
    rowHashtags(item) {
      if (!item?.entityPk || !this.feedApp) {
        return [];
      }
      let names = null;
      let idList = "";
      try {
        names = this.feedApp.getEntityField(this.rowEntityType, item.entityPk, HASHTAG_NAMES_FIELD);
        idList = this.feedApp.getEntityField(this.rowEntityType, item.entityPk, HASHTAG_IDS_FIELD) ?? "";
      } catch {
        return [];
      }
      if (typeof names === "string") {
        try {
          names = JSON.parse(names);
        } catch {
          return [];
        }
      }
      if (!Array.isArray(names)) {
        return [];
      }
      const ids = String(idList).split(HASHTAG_ID_DELIMITER).filter(part => part !== "").map(Number);
      return names.map((entry, index) => {
        if (entry && typeof entry === "object") {
          return { hashtagId: Number(entry.hashtagId), value: String(entry.value ?? "") };
        }
        return { hashtagId: ids[index], value: String(entry ?? "") };
      });
    },

    selectFilterValue(value) {
      if (this.editMode) {
        return;
      }
      this.activeFilterValue = value ?? "";
    },

    loadMoreNext() {
      if (this.editMode || !this.feedContext.entityType || !this.feedContext.pk) {
        return;
      }
      const app = App.get(this.contextAppName);
      const context = this.feedContext;
      app.loadMoreNext(context.entityType, context.pk, this.fieldName, null, this.activeFilters);
    },

    rowStoryType(item) {
      if (!this.isStoryFeed) {
        return "";
      }
      let entityStoryType = "";
      if (item?.entityPk) {
        try {
          entityStoryType = App.get(this.contextAppName)?.getEntityField(this.rowEntityType, item.entityPk, "storyType") ?? "";
        } catch {
          entityStoryType = "";
        }
      }
      return FeedComponentsMap.rowStoryType({
        itemStoryType: item?.storyType,
        entityStoryType,
        feedStoryType: this.feedStoryType,
      });
    },
    rowTypeKey(item) {
      const fieldName = this.rowTypeFieldName;
      if (!fieldName) {
        return this.rowStoryType(item);
      }
      if (!item?.entityPk || !this.rowEntityType) {
        return "";
      }
      try {
        const value = App.get(this.contextAppName)
            ?.getEntityField(this.rowEntityType, item.entityPk, fieldName);
        return value === null || value === undefined ? "" : String(value);
      } catch {
        return "";
      }
    },
    separatorScopeEntity(item) {
      if (!item?.entityPk) {
        return null;
      }
      try {
        const entity = App.get(this.contextAppName)?.getEntity(item.entityType, item.entityPk);
        const fields = entity?.getBasicFieldData?.() ?? {};
        return { ...fields, entityType: item.entityType, entityPk: item.entityPk };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `feed separator scope for ${item.entityType}:${item.entityPk} could not be read, `
          + `so the row is untestable: ${error.message}`,
        );
        return null;
      }
    },
    pickSeparatorConfig(beforeItem, afterItem, isLeading) {
      const scope = {
        before: beforeItem ? this.separatorScopeEntity(beforeItem) : null,
        after: afterItem ? this.separatorScopeEntity(afterItem) : null,
      };
      for (let i = 0; i < this.separatorConfigs.length; i++) {
        const config = this.separatorConfigs[i];
        const expression = isLeading
          ? config.leadingExpression || config.visibilityExpression
          : config.visibilityExpression;
        try {
          if (LogicExpression.evaluate(expression, scope) === true) {
            return config;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            `feed separator rule[${i}] ("${config?.context?.separatorLabel?.value ?? ""}") failed on `
            + `${beforeItem?.pkString ?? "start"}→${afterItem?.pkString ?? "end"}: ${e.message}`,
          );
        }
      }
      return null;
    },
    captureReadPosition() {
      const pointer = this.readPositionPointer;
      if (this.readPosition !== null || !pointer?.ready) {
        return;
      }
      const items = this.entities;
      if (!readPositionDecided(items, pointer.pk, { hasMorePrevious: false, hasMoreNext: this.hasMoreNext })) {
        return;
      }
      const anchorPk = readPositionAnchor(items, pointer.pk);
      this.readPosition = { anchorPkString: anchorPk ? App.getPkString(anchorPk) : null };
      if (anchorPk) {
        this.$nextTick(() => this.scrollToReadPosition());
      }
      const action = String(this.getOptionValue(FeedReadPositionAction) ?? "");
      if (action && readPositionShouldMarkRead(items, pointer, anchorPk)) {
        App.getRoot()
            .getEntity(this.feedContext.entityType, this.feedContext.pk, this.contextAppName)
            .triggerAction(action, {
              [this.entityContext]: { entityType: this.feedContext.entityType, pk: this.feedContext.pk },
            });
      }
    },
    scrollToReadPosition() {
      const separator = this.$el?.querySelector?.("[data-read-position-separator]");
      separator?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    },
    separatorRowEntry(entity) {
      return { kind: "row", entity, key: this.stableRowKey(entity) };
    },
    stableRowKey(entity) {
      return this.rowKeys(entity);
    },
    separatorEntry(config, afterItem, suffix) {
      return {
        kind: "section",
        label: this.separatorLabelFor(config, afterItem),
        componentId: config.template,
        key: `section-${suffix}-${this.stableRowKey(afterItem)}`,
      };
    },
    previewSeparatorLabel(text) {
      const now = dayjs();
      return previewLabel(text, pattern => now.format(pattern));
    },
    separatorLabelFor(config, afterItem) {
      const parts = config?.labelParts;
      const typed = config?.context?.separatorLabel?.value ?? "";
      if (!Array.isArray(parts) || parts.length === 0) {
        return typed;
      }
      const scope = { [LABEL_SCOPE]: this.separatorScopeEntity(afterItem) };
      try {
        const resolved = parts
            .map(part => {
              const value = resolveOneParam(part, scope);
              return value === undefined || value === null ? "" : String(value);
            })
            .join("");
        return resolved.trim() === "" ? typed : resolved;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`feed separator label failed on ${afterItem?.pkString ?? "?"}: ${e.message}`);
        return typed;
      }
    },
    rowIsHydrated(item) {
      if (!item?.entityPk || !this.rowEntityType) {
        return true;
      }
      try {
        const entity = App.get(this.contextAppName)?.getEntity(this.rowEntityType, item.entityPk);
        return !entity || Boolean(entity.isHydrated);
      } catch {
        return true;
      }
    },
    rowLeaf(item) {
      return FeedComponentsMap.resolve(this.feedComponentsMap, {
        entityType: this.rowEntityType,
        type: this.rowTypeKey(item),
        subtype: this.rowCompletionStatus(item),
      });
    },
    fieldDefRowContexts(item) {
      const map = this.fieldDefRowContext;
      if (!map) {
        return {};
      }
      const scope = this.rowSelectorScope(item);
      const resolved = {};
      for (const [contextName, sourceDef] of Object.entries(map)) {
        try {
          const value = resolveOneParam(sourceDef, scope);
          if (value && value.entityType && (value.pk || value.entityPk)) {
            resolved[contextName] = { entityType: value.entityType, pk: value.pk ?? value.entityPk };
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`rowContext "${contextName}" on ${this.fieldName} failed: ${e.message}`);
        }
      }
      return resolved;
    },
    rowSelectorScope(item) {
      const index = this.entities.findIndex(row => row?.entityPk?.id === item?.entityPk?.id);
      return {
        ...this.enclosingRowContexts,
        before: index > 0 ? this.separatorScopeEntity(this.entities[index - 1]) : null,
        after: index >= 0 && index < this.entities.length - 1
          ? this.separatorScopeEntity(this.entities[index + 1]) : null,
        current: this.separatorScopeEntity(item),
      };
    },
    matchingSelectorFor(item) {
      const scope = this.rowSelectorScope(item);
      for (let i = 0; i < this.fieldDefRowSelectors.length; i++) {
        const config = this.fieldDefRowSelectors[i];
        try {
          if (LogicExpression.evaluate(config.visibilityExpression, scope)) {
            return config;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`rowComponentSelector[${i}] visibility error on ${this.fieldName}: ${e.message}`);
        }
      }
      return null;
    },
    selectorComponentIdFor(item) {
      const config = this.matchingSelectorFor(item);
      return config ? this.resolveDestinationComponentIdByName(config.template) : 0;
    },
    resolveDestinationComponentIdByName(targetName) {
      if (!targetName) {
        return 0;
      }
      if (isCompiledSurfaceKind(renderSurfaceKind())) {
        return Number(window.blclient?.data?.destinationComponents?.[targetName]) || 0;
      }
      const store = getActivePinia()?._s?.get?.("app-CreatorStudio-entities-DynamicComponent");
      const rows = Object.values(store?.$state?.entities ?? {});
      const match = rows.find(row => row?.fieldData?.name === targetName);
      return Number(match?.fieldData?.id) || 0;
    },
    rowComponentId(item) {
      const conditional = this.editMode ? 0 : conditionComponentId(
        this.getOptionValue(FeedRowConditions),
        this.rowSelectorScope(item),
        (expression, scope) => LogicExpression.evaluate(expression, scope),
      );
      if (conditional > 0) {
        return conditional;
      }
      const hit = this.rowLeaf(item);
      if (hit) {
        return hit.leaf.componentId;
      }
      const selected = this.selectorComponentIdFor(item);
      if (selected > 0) {
        return selected;
      }
      const problems = [this.unmappedRowProblem(item)];
      if (isCompiledSurfaceKind(renderSurfaceKind())) {
        // XXX: intentional hard fail — do NOT catch or soften. No leaf and no
        // XXX: reachable default means the author never mapped a component for
        // XXX: this row; rendering nothing hides it. Add the override or a
        // XXX: "default" beside it, then recompile.
        throw new ContextValidationError("feed", problems);
      }
      return 0;
    },
    unmappedRowProblem(item) {
      const coordinate = [
        FeedComponentsMap.entityKey(this.rowEntityType),
        this.rowStoryType(item),
        this.rowCompletionStatus(item),
      ].filter(Boolean).join(".");
      return `no component mapped for "${coordinate}" and no reachable `
        + `"${FeedComponentsMap.DEFAULT_KEY}" above it in ${FeedComponentsMap.OPTION_KEY}`;
    },
    rowCompletionStatus(item) {
      if (this.editMode || this.inBuilderEditScope) {
        return "";
      }
      try {
        const storyId = Number(item?.entityPk?.id) || 0;
        if (!storyId) {
          return "";
        }
        const rowEntity = App.get(this.contextAppName)?.getEntity(this.rowEntityType, { id: String(storyId) });
        const store = useCompletionStatusStore();
        const rawStatus = resolveCompletionStatus({
          entity: rowEntity,
          storyId,
          statusByStory: store.statusByStory,
          progressByFolder: store.progressByFolder,
        });
        return useCompletionAssignmentStore().normalizeStatus(this.completionPublicationId, rawStatus);
      } catch {
        return "";
      }
    },
    getItemContexts(item) {
      if (this.editMode) {
        return;
      }
      if (!this.feedMapGatePasses) {
        return {};
      }
      const hit = this.rowLeaf(item);
      if (!hit) {
        return this.fieldDefRowContexts(item);
      }
      const { contexts, problems } = FeedComponentsMap.resolveRowContexts({
        leaf: hit.leaf,
        row: { entityType: item.entityType, pk: item.entityPk, appName: this.contextAppName },
        enclosingContexts: this.enclosingRowContexts,
        walkField: (entityType, pk, fieldName) => App.get(this.contextAppName)
            .getEntityField(entityType, pk, fieldName),
      });
      const requiredDefs = readRequiredContexts(
        DynamicComponent.get(Number(hit.leaf.componentId), this.renderingInProduction),
      ) ?? {};
      const ambient = ambientContextNames();
      const mustBeSourced = Object.entries(requiredDefs)
          .filter(([name, definition]) => !ambient.has(name)
            && !Object.prototype.hasOwnProperty.call(definition ?? {}, "default"))
          .map(([name]) => name);
      const allProblems = [
        ...problems,
        ...FeedComponentsMap.missingSourcingProblems(hit.leaf, mustBeSourced),
        ...FeedComponentsMap.kindMismatchProblems(hit.leaf, requiredDefs),
      ];
      if (allProblems.length && isCompiledSurfaceKind(renderSurfaceKind())) {
        // XXX: intentional hard fail — do NOT catch or soften. A row mounted
        // XXX: with an unsourced or unresolvable required context renders a
        // XXX: silently empty card. Fix the sourcing in the inspector.
        throw new ContextValidationError(`feed row component ${hit.leaf.componentId}`, allProblems);
      }
      return buildComponentContexts({ ...this.enclosingRowContexts, ...contexts }, requiredDefs);
    },
  },
};
</script>
